import * as React from 'react';

import { BaseButton, Button, IButton } from '@fluentui/react';
import { ITheme } from '@fluentui/react/lib/Styling';

import { getInputMethodFromEvent, InputMethod, isIE } from '@ms/acui-common';
import { AppThemeConsumer } from '@ms/acui-theme';

import { AppBaseButtonProps } from './AppBaseButton.Props';
import { getAppBaseButtonStyles } from './AppBaseButton.styles';

export class AppBaseButton extends React.PureComponent<AppBaseButtonProps, {}> {
  private button = React.createRef<IButton>();
  private pointerDownEventId: number | undefined = undefined;
  private pointerInputMethod: InputMethod | undefined = undefined;

  public render() {
    return (
      <AppThemeConsumer>
        {(theme: ITheme) => (
          <BaseButton
            {...this.props}
            componentRef={this.button}
            onMouseDown={this.onMouseDown}
            styles={getAppBaseButtonStyles(theme, this.props.styles, this.props.iconProps !== undefined)}
            allowDisabledFocus={true}
            onPointerDown={this.onPointerDown}
            onPointerUp={this.onPointerUp}
          />
        )}
      </AppThemeConsumer>
    );
  }

  public openMenu = (shouldFocusOnContainer?: boolean, shouldFocusOnMount?: boolean) => {
    if (this.button.current) {
      this.button.current.openMenu(shouldFocusOnContainer, shouldFocusOnMount);
    }
  };

  public dismissMenu = () => {
    if (this.button.current) {
      this.button.current.dismissMenu();
    }
  };

  public focus = () => {
    this.button.current && this.button.current.focus();
  };

  public getInputMethod = (ev?: React.MouseEvent<Button>): InputMethod | undefined => {
    let inputMethod = this.pointerInputMethod;

    // if inputMethod is not Touch or Pen and we have a mouse event, lets attempt to get
    // the input method from event instead of the pointer.  In cases where the event is undefined
    // we will always use pointer.  For example, flyout anchors don't pass an event.
    // However, IE will have ev.native always as 0 which will make us set
    // inputMethod for clicks to Keyboard, we know this is a mouse click so lets just return
    // the correct inputMethod
    if (
      ev &&
      inputMethod !== InputMethod.Pen &&
      inputMethod !== InputMethod.Touch &&
      !(isIE() && inputMethod === InputMethod.Mouse)
    ) {
      inputMethod = getInputMethodFromEvent(ev);
    }

    this.pointerInputMethod = undefined;
    return inputMethod;
  };

  private onMouseDown = (ev: React.MouseEvent<BaseButton>) => {
    if (this.props.onMouseDown) {
      this.props.onMouseDown(ev);
    }

    if (!this.props.takeFocusOnClick) {
      ev.preventDefault();
    }
  };

  private onPointerDown = (ev: React.PointerEvent<HTMLElement>) => {
    this.pointerDownEventId = ev.pointerId;
  };

  private onPointerUp = (ev: React.PointerEvent<HTMLElement>) => {
    if (this.pointerDownEventId === ev.pointerId) {
      switch (ev.pointerType) {
        case 'touch':
          this.pointerInputMethod = InputMethod.Touch;
          break;
        case 'pen':
          this.pointerInputMethod = InputMethod.Pen;
          break;
        case 'mouse':
          this.pointerInputMethod = InputMethod.Mouse;
          break;
        default:
          this.pointerInputMethod = InputMethod.Unknown;
      }
    }
    this.pointerDownEventId = undefined;
  };
}
